<template>
  <section
    :class="sectionClasses"
    class="moments-list section section--bg-color-primary"
  >
    <div class="moments-list__container section__container">
      <template v-if="listedMoments.length">
        <ul
          class="moments-list__list section__content section__content--listing section__content--thirds"
        >
          <li
            v-for="moment in visibleMoments"
            :key="moment.urlSegment ?? moment.id"
            class="moments-list__item site-page-partial section__item"
          >
            <article
              class="site-page-partial__content"
              @click="onCardClick"
            >
              <figure
                v-if="moment.imageHtml"
                class="site-page-partial__image-container"
                v-html="moment.imageHtml"
              ></figure>

              <div v-else class="site-page-partial__no-image-container">
                <span class="site-page-partial__no-image-container-graphic"></span>
              </div>

              <div class="site-page-partial__text-container">
                <div
                  v-if="getMomentCategories(moment).length || moment.year"
                  class="site-page-partial__meta"
                >
                  <div
                    v-if="getMomentCategories(moment).length"
                    class="site-page-partial__meta-categories tag-container"
                  >
                    <span
                      v-for="category in getMomentCategories(moment)"
                      :key="category"
                      class="site-page-partial__meta-category tag tag--small"
                    >
                      {{ category }}
                    </span>
                  </div>

                  <div v-if="moment.year" class="site-page-partial__year">
                    {{ moment.year }}
                  </div>
                </div>

                <h3 class="site-page-partial__heading heading heading--small">
                  <a class="site-page-partial__link" :href="moment.url || '#'">
                    {{ moment.title }}
                  </a>
                </h3>

                <p v-if="moment.momentExcerpt" class="site-page-partial__excerpt">
                  {{ moment.momentExcerpt }}
                </p>

                <div class="site-page-partial__arrow-icon-container">
                  <span class="site-page-partial__arrow-icon icon icon--arrow-right"></span>
                </div>
              </div>
            </article>
          </li>
        </ul>

        <div v-if="remainingCount > 0" class="moments-list__show-more show-more">
          <button
            type="button"
            :class="showMoreButtonClasses"
            @click="showMoreMoments"
          >
            <span :class="showMoreIconClasses"></span>
            <span class="moments-list__show-more-text">
              {{ showMoreLabel }}
            </span>
          </button>
        </div>
      </template>

      <div v-else class="moments-list__empty no-content">
        <p>{{ store.translations.noPages }}</p>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import type { ITimelineMapItem } from "@features/Pages/TimelineMapPage/_js/app/interfaces/ITimelineMapItem";
import { useTimelineMapStore } from "@features/Pages/TimelineMapPage/_js/app/store/timelineMapStore";

const store = useTimelineMapStore();
const initialVisibleMoments = 9;
const momentsBatchSize = 9;
const visibleCount = ref(initialVisibleMoments);

const currentLanguage = (
  document.getElementById("js-current-language") as HTMLInputElement | null
)?.value ?? document.documentElement.lang ?? "sv";

const collator = new Intl.Collator(
  currentLanguage.startsWith("en") ? "en" : "sv",
  { sensitivity: "base" }
);

const sectionClasses = computed(() => {
  if (store.isVRAK) {
    return ["section--site-page-partial-color-black-100-vrak"];
  }

  if (store.isMM) {
    return ["section--site-page-partial-color-bremon-100-mm"];
  }

  if (store.isJVM) {
    return ["section--site-page-partial-color-light-green-blue"];
  }

  return [];
});

const useLinkStyle = computed(() => store.isMM || store.isVRAK);

const showMoreButtonClasses = computed(() => [
  "moments-list__show-more-button",
  useLinkStyle.value ? "link" : "button button--secondary",
]);

const showMoreIconClasses = computed(() => [
  useLinkStyle.value ? "link__icon" : "button__icon",
  "icon",
  "icon--plus",
]);

const listedMoments = computed(() => {
  const momentsById = new Map<number, ITimelineMapItem>();

  store.epochs
    .flatMap((epoch) => epoch.timelineMapItems ?? [])
    .filter((item) => item.type === "Moment")
    .forEach((item) => {
      momentsById.set(item.id, item);
    });

  return Array.from(momentsById.values())
    .sort((left, right) => collator.compare(left.title ?? "", right.title ?? ""));
});

const visibleMoments = computed(() => listedMoments.value.slice(0, visibleCount.value));

const remainingCount = computed(() => Math.max(listedMoments.value.length - visibleCount.value, 0));

const fallbackShowMoreText = currentLanguage.startsWith("en") ? "Show more" : "Visa fler";

const showMoreText = computed(() => {
  const translation = store.translations.showMore;
  return typeof translation === "string" && translation.trim().length > 0
    ? translation
    : fallbackShowMoreText;
});

const showMoreLabel = computed(() => {
  const parts = showMoreText.value.trim().split(/\s+/);

  if (parts.length >= 2) {
    return `${parts[0]} ${remainingCount.value} ${parts.slice(1).join(" ")}`;
  }

  return `${showMoreText.value} ${remainingCount.value}`;
});

function onCardClick(event: MouseEvent) {
  if (!(event.currentTarget instanceof HTMLElement)) {
    return;
  }

  if (event.target instanceof Element && event.target.closest(".site-page-partial__link")) {
    return;
  }

  const link = event.currentTarget.querySelector(".site-page-partial__link");
  const url = link?.getAttribute("href");

  if (!url || url === "#") {
    return;
  }

  window.location.assign(url);
}

function showMoreMoments() {
  visibleCount.value = Math.min(visibleCount.value + momentsBatchSize, listedMoments.value.length);
}

function getMomentCategories(moment: ITimelineMapItem): string[] {
  if (Array.isArray(moment.categories) && moment.categories.length) {
    return moment.categories.filter(
      (category): category is string => typeof category === "string" && category.trim().length > 0
    );
  }

  if (typeof moment.category === "string" && moment.category.trim().length > 0) {
    return [moment.category];
  }

  return [];
}
</script>
